import { Box } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { type BackgroundCheckMissingDataType } from "@src/appV2/OpenShifts/ShiftAction/types";

import { LoadingButton } from "../../components/LoadingButton";
import { useShiftModalsDataContext } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { useWorkplaceHasBookingRequestsEnabled } from "../../WorkerShifts/BookingRequests/api/useWorkplaceHasBookingRequestsEnabled";
import { convertModalShiftDataToUseBookShiftParams } from "../Booking/convertModalShiftDataToUseBookShiftParams";
import { useBookShift } from "../Booking/useBookShift";
import { type ModalShiftData } from "../types";

interface BookShiftButtonProps {
  onBook: () => void;
  disabled?: boolean;
  isInstantBookDisabled?: boolean;
  onDisabledButtonClick?: () => void;
  shift: ModalShiftData;
  missingBackgroundCheckDataTypes?: BackgroundCheckMissingDataType[];
  fullWidth?: boolean;
  rateNegotiationId: string | undefined;
}

function getButtonText(parameters: {
  rateNegotiationPayRate?: number;
  disabled?: boolean;
  isBookingRequest: boolean;
}): string {
  const { rateNegotiationPayRate, disabled, isBookingRequest } = parameters;

  if (disabled && !isBookingRequest) {
    return "Can't book";
  }

  if (disabled) {
    return "Can't request";
  }

  if (rateNegotiationPayRate) {
    return `Book at ${formatDollarsAsUsd(rateNegotiationPayRate)}/hr`;
  }

  return "Book";
}

// TODO: Following features need to be ported: Rate negotiation, request non-IP shift, conflicting shift invites
export function BookShiftButton(props: BookShiftButtonProps) {
  const {
    onBook,
    disabled,
    isInstantBookDisabled = false,
    onDisabledButtonClick,
    shift,
    missingBackgroundCheckDataTypes = [],
    fullWidth = false,
    rateNegotiationId,
  } = props;
  const {
    id: shiftId,
    workplace: { id: workplaceId },
    rateNegotiation,
  } = shift;

  const { navigateToModal, computedArrivalData } = useShiftModalsDataContext();

  const bookShiftParams = convertModalShiftDataToUseBookShiftParams({
    shift,
    navigateToModal,
    onBook,
    computedArrivalData,
    rateNegotiationId,
  });

  const rateNegotiationPayRate = rateNegotiationId
    ? rateNegotiation?.lastOffer?.hourlyPay
    : undefined;

  const { attemptBookingShift, isBooking, isLoading } = useBookShift(bookShiftParams);

  const {
    bookingRequestsEnabled: canSeeWorkplaceBookingRequest,
    isLoading: isLoadingBookingRequestsEnabled,
  } = useWorkplaceHasBookingRequestsEnabled(workplaceId);
  const isBookingRequest = isInstantBookDisabled && canSeeWorkplaceBookingRequest;

  const buttonContent = (
    <LoadingButton
      fullWidth={fullWidth}
      size="large"
      variant="contained"
      disabled={disabled}
      isLoading={isLoading || isBooking || isLoadingBookingRequestsEnabled}
      sx={{
        minWidth: "30%",
      }}
      onClick={async () => {
        logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_CTA_BOOK_SHIFT, { shiftId });
        await attemptBookingShift({
          missingBackgroundCheckDataTypes,
        });
      }}
    >
      {getButtonText({ rateNegotiationPayRate, disabled, isBookingRequest })}
    </LoadingButton>
  );

  // When disabled, wrap in a clickable container to capture clicks
  if (disabled) {
    return <Box onClick={onDisabledButtonClick}>{buttonContent}</Box>;
  }

  // When enabled, return the button directly
  return buttonContent;
}
